"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MeasurementService = void 0;
const three_1 = require("three");
const intersection_1 = require("../webgl/services/intersection");
const Webgl_1 = require("../webgl/Webgl");
const label_1 = require("../measurement/components/label");
const calculation_1 = require("../measurement/components/calculation");
const mouse_1 = require("../webgl/services/mouse");
const config_1 = require("../../config");
const color_1 = require("../measurement/components/color");
const point_1 = require("../measurement/components/point");
const pubsub_js_1 = require("pubsub-js");
const MAX_LINES_ALLOWED = config_1.config.measurement.maxLinesAllowed;
class MeasurementService {
    constructor() {
        this.maxLinesAllowed = MAX_LINES_ALLOWED;
        this.pointerStartPosition = new three_1.Vector2();
        this.pointerClickEnabled = false;
        this.pointerMoveEnabled = false;
        this.isFirstPoint = true;
        this.measurements = [];
        this.pointIdx = 0;
        this.points = [];
        this.mouse = new three_1.Vector2();
        this.lineId = 0;
        this._isMeasuring = false;
        this.transformatorType = {
            transformatorConversionType: 6 /* TO_METER */,
            unit: "m"
        };
        this.measurementFunctions = {
            pointerDown: (event) => this.onPointerDown(event),
            pointerUp: (event) => this.onPointerUp(event),
            pointerMove: (event) => this.onPointerMove(event),
            keyUp: (event) => this.onKeyUp(event)
        };
        this.webgl = Webgl_1.Webgl.getInstance();
        this.intersections = new intersection_1.Intersections();
        (0, pubsub_js_1.subscribe)("change-measurement-type" /* CHANGE_MEASUREMENT_TYPE */, (_, data) => {
            this.transformatorType = data;
        });
    }
    get isMeasuring() { return this._isMeasuring; }
    set isMeasuring(isMeasuring) { this._isMeasuring = isMeasuring; }
    disable() {
        this.clearOnFail();
        this.clearAndDeselectMeasurement();
    }
    dispose() {
        var _a;
        this.clear();
        this.removeEvents();
        (_a = this.measurementControls) === null || _a === void 0 ? void 0 : _a.dispose();
        delete this.measurementControls;
    }
    clear() {
        this.stopMeasurement();
        this.clearAndDeselectMeasurement();
        this.measurements.forEach((measurement) => this.webgl.removeObject(measurement));
        // this.measurements = [];
        // this.points = [];
    }
    removeEvents() {
        this.webgl.renderer.domElement.removeEventListener('mousedown', this.measurementFunctions.pointerDown);
        this.webgl.renderer.domElement.removeEventListener('mousemove', this.measurementFunctions.pointerMove);
        this.webgl.renderer.domElement.removeEventListener('mouseup', this.measurementFunctions.pointerUp);
        document.removeEventListener('keyup', this.measurementFunctions.keyUp);
    }
    onPointerDown(event) {
        if (!this.pointerClickEnabled)
            return;
        this.mouse = (0, mouse_1.mousePosition)(event, this.webgl.renderer.domElement);
        this.pointerStartPosition = this.mouse.clone();
    }
    onPointerMove(event) {
        var _a, _b;
        if (!this.pointerMoveEnabled) {
            return;
        }
        this.mouse = (0, mouse_1.mousePosition)(event, this.webgl.renderer.domElement);
        // Only a model should be included in measurement
        const modelOnly = this.webgl.managableObjects.find(o => o.type === 'Mesh');
        const intersections = this.intersections.getIntersections(this.mouse, [modelOnly], this.webgl.camera, true);
        if (!intersections || !this.line) {
            return;
        }
        this.line.computeLineDistances();
        const positions = (_a = this.line) === null || _a === void 0 ? void 0 : _a.geometry.attributes.position.array;
        positions[this.pointIdx + 3] = intersections[0].point.x;
        positions[this.pointIdx + 4] = intersections[0].point.y;
        positions[this.pointIdx + 5] = intersections[0].point.z;
        this.line.geometry.attributes.position.needsUpdate = true;
        const lineLabel = (_b = this.measurementGroup) === null || _b === void 0 ? void 0 : _b.getObjectByName('lineLabel_' + this.lineId);
        if (!lineLabel) {
            return;
        }
        switch (this.measureType) {
            case 1 /* DISTANCE */:
                this.refreshDistance(lineLabel, positions, this.pointIdx);
                break;
            case 2 /* HEIGHT */:
                this.refreshHeight(lineLabel, positions, this.pointIdx);
                break;
            case 3 /* ANGLE */:
                this.refreshAngle(lineLabel, positions, this.pointIdx);
                break;
            case 4 /* AREA */:
                this.refreshArea(lineLabel, positions, this.pointIdx);
                break;
            default: throw new Error("Measure type is undefined");
        }
    }
    onPointerUp(event) {
        if (!this.pointerClickEnabled) {
            return;
        }
        this.webgl.renderer.domElement.style.cursor = this.isMeasuring ? 'crosshair' : 'grab';
        this.mouse = (0, mouse_1.mousePosition)(event, this.webgl.renderer.domElement);
        if (!this.pointerStartPosition.equals(this.mouse)) {
            // In case orbit is rotated, at end it will stop rendering, so there rendering should be started again
            this.webgl.startRendering();
            return;
        }
        // Do not include lights when measuring
        //@ts-ignore
        const managableObjects = this.webgl.managableObjects.filter(o => !o.light);
        const intersections = this.intersections.getIntersections(this.mouse, managableObjects, this.webgl.camera, false);
        if (!intersections) {
            return;
        }
        this.webgl.orbitControls.enabled = false;
        this.pointerMoveEnabled = false;
        let isEnd = false;
        switch (this.measureType) {
            case 1 /* DISTANCE */:
                isEnd = this.distance(intersections[0]);
                break;
            case 2 /* HEIGHT */:
                isEnd = this.height(intersections[0]);
                break;
            case 3 /* ANGLE */:
                isEnd = this.angle(intersections[0]);
                break;
            case 4 /* AREA */:
                isEnd = this.area(intersections[0]);
                break;
            default: throw new Error("Measure type is undefined");
        }
        if (isEnd) {
            this.webgl.renderOnce();
        }
        else {
            this.webgl.startRendering();
            this.pointerMoveEnabled = true;
            this.webgl.orbitControls.enabled = true;
        }
    }
    onKeyUp(event) {
        if (event.key === 'Escape') {
            // If no points are drawn and user presses escape
            // stop measuring and deselect measurement type icon
            if (!this.points.length) {
                this.stopMeasurement(false);
                this.diselectRadioOption();
                return;
            }
            this.clearOnFail();
            this.stopMeasurement();
        }
    }
    measurementStart(measureType) {
        this.webgl.renderer.domElement.addEventListener('mousedown', this.measurementFunctions.pointerDown);
        this.webgl.renderer.domElement.addEventListener('mousemove', this.measurementFunctions.pointerMove);
        this.webgl.renderer.domElement.addEventListener('mouseup', this.measurementFunctions.pointerUp);
        document.addEventListener('keyup', this.measurementFunctions.keyUp);
        this._isMeasuring = true;
        switch (measureType) {
            case 1 /* DISTANCE */:
                this.maxLinesAllowed = MAX_LINES_ALLOWED;
                break;
            case 2 /* HEIGHT */:
                this.maxLinesAllowed = 2;
                break;
            case 3 /* ANGLE */:
                this.maxLinesAllowed = 2;
                break;
            // case MeasureType.AREA: this.maxLinesAllowed = MAX_LINES_ALLOWED; break;
            //Area lines limited to 5 only temporarily
            case 4 /* AREA */:
                this.maxLinesAllowed = 5;
                break;
            default: throw new Error("Measure type is undefined");
        }
        this.measureType = measureType;
        this.pointerClickEnabled = true;
        this.webgl.renderer.domElement.style.cursor = 'crosshair';
        this.measurementGroup = new three_1.Group();
    }
    clearAndDeselectMeasurement() {
        this.stopMeasurement();
        this.webgl.stopRendering();
        this.measureType = undefined;
        this.webgl.renderer.domElement.removeEventListener('mousedown', this.measurementFunctions.pointerDown);
        this.webgl.renderer.domElement.removeEventListener('mousemove', this.measurementFunctions.pointerMove);
        this.webgl.renderer.domElement.removeEventListener('mouseup', this.measurementFunctions.pointerUp);
        document.removeEventListener('keyup', this.measurementFunctions.keyUp);
        this.webgl.renderer.domElement.style.cursor = 'grab';
        this.webgl.orbitControls.enabled = true;
        this.diselectRadioOption();
    }
    stopMeasurement(reSelect = true) {
        this._isMeasuring = false;
        this.points = [];
        this.line = undefined;
        this.isFirstPoint = true;
        this.pointerClickEnabled = false;
        this.pointerMoveEnabled = false;
        this.measureType = undefined;
        this.webgl.renderer.domElement.style.cursor = 'grab';
        this.webgl.orbitControls.enabled = true;
        this.webgl.stopRendering();
        this.webgl.renderer.domElement.removeEventListener('mousedown', this.measurementFunctions.pointerDown);
        this.webgl.renderer.domElement.removeEventListener('mousemove', this.measurementFunctions.pointerMove);
        this.webgl.renderer.domElement.removeEventListener('mouseup', this.measurementFunctions.pointerUp);
        document.removeEventListener('keyup', this.measurementFunctions.keyUp);
        if (reSelect) {
            this.reselectRadioOption();
        }
    }
    reselectRadioOption() {
        const controls = document.getElementById('measurementControls');
        if (!controls) {
            return;
        }
        const checkedRadioEl = controls.querySelector('input[type=radio]:checked');
        if (checkedRadioEl) {
            checkedRadioEl.click();
        }
    }
    diselectRadioOption() {
        const controls = document.getElementById('measurementControls');
        if (!controls) {
            return;
        }
        const checkedRadioEl = controls.querySelector('input[type=radio]:checked');
        if (checkedRadioEl) {
            checkedRadioEl.checked = false;
        }
    }
    clearOnFail() {
        var _a, _b;
        if (!this.line) {
            return;
        }
        this.setPreviousLinePoints(this.line);
        const lineLabel = (_a = this.measurementGroup) === null || _a === void 0 ? void 0 : _a.getObjectByName('lineLabel_' + this.lineId);
        if (lineLabel) {
            this.webgl.removeObject(lineLabel);
        }
        if (this.lineId === 0 || this.measureType === 3 /* ANGLE */ || this.measureType === 4 /* AREA */) {
            const linePoint = (_b = this.measurementGroup) === null || _b === void 0 ? void 0 : _b.getObjectByName('linePoint_' + this.lineId);
            if (!linePoint.parent) {
                return;
            }
            const idx = this.measurements.findIndex((measurement) => { var _a; return measurement.uuid === ((_a = linePoint.parent) === null || _a === void 0 ? void 0 : _a.uuid); });
            if (idx === -1) {
                return;
            }
            this.measurements.splice(idx, 1);
            this.webgl.removeObject(linePoint.parent);
        }
    }
    setInitialLinePoints(measureType, intersection, points, maxLinesAllowed) {
        const numberOfPoints = maxLinesAllowed + 1;
        const color = (0, color_1.selectColor)(measureType);
        for (let index = 0; index < numberOfPoints; index++) {
            points.push(intersection.point.clone());
        }
        const geometry = new three_1.BufferGeometry().setFromPoints(points);
        const material = new three_1.LineBasicMaterial({
            color,
            depthTest: false,
            depthWrite: false
        });
        return new three_1.Line(geometry, material);
    }
    setNextLinePoints(intersection, line) {
        var _a;
        const positions = ((_a = this.line) === null || _a === void 0 ? void 0 : _a.geometry.attributes.position.array) || [];
        // Set next point and override rest of points to the last point value
        this.pointIdx += 3;
        for (let index = this.pointIdx; index < positions.length;) {
            const point = intersection.point.clone();
            positions[index++] = point.x;
            positions[index++] = point.y;
            positions[index++] = point.z;
        }
        line.geometry.attributes.position.needsUpdate = true;
    }
    setPreviousLinePoints(line) {
        var _a;
        const positions = ((_a = this.line) === null || _a === void 0 ? void 0 : _a.geometry.attributes.position.array) || [];
        const point = new three_1.Vector3(positions[this.pointIdx + 0], positions[this.pointIdx + 1], positions[this.pointIdx + 2]);
        // Set previous point and override rest of points to the previous point value
        for (let index = this.pointIdx; index < positions.length;) {
            positions[index++] = point.x;
            positions[index++] = point.y;
            positions[index++] = point.z;
        }
        line.geometry.attributes.position.needsUpdate = true;
    }
    resetPoints() {
        this.pointIdx = 0;
        this.lineId = 0;
        this.points = [];
    }
    refreshDistance(lineLabel, positions, pointIdx) {
        const labelPosition = (0, label_1.distanceLabelPositionCalculation)(positions, pointIdx);
        lineLabel.position.copy(labelPosition);
        const distance = (0, calculation_1.distanceCalculation)(positions, pointIdx);
        lineLabel.element.innerHTML = distance.toFixed(2) + this.transformatorType.unit;
    }
    refreshHeight(lineLabel, positions, pointIdx) {
        if (!this.line) {
            throw new Error("Line is undefined");
        }
        const end = [positions[3], positions[4], positions[5]];
        positions[6] = end[0];
        positions[7] = end[1];
        positions[8] = end[2];
        positions[4] = positions[1]; // override corner y with start y
        const labelPosition = (0, label_1.heightLabelPositionCalculation)(positions);
        lineLabel.position.copy(labelPosition);
        this.line.geometry.attributes.position.needsUpdate = true;
        const height = (0, calculation_1.heightCalculation)(positions, pointIdx);
        lineLabel.element.innerHTML = height.toFixed(2) + this.transformatorType.unit;
    }
    refreshAngle(lineLabel, positions, pointIdx) {
        const labelPosition = (0, label_1.angleLabelPositionCalculation)(positions);
        lineLabel.position.copy(labelPosition);
        const angle = (0, calculation_1.angleCalculation)(positions);
        lineLabel.element.innerHTML = angle.toFixed(2) + '°'; // TODO: maybe deg sign is not correct (rad???)
    }
    refreshArea(lineLabel, positions, pointIdx) {
        const distanceLabelPosition = (0, label_1.distanceLabelPositionCalculation)(positions, pointIdx);
        lineLabel.position.copy(distanceLabelPosition);
        const area = (0, calculation_1.distanceCalculation)(positions, pointIdx);
        lineLabel.element.innerHTML = area.toFixed(2) + this.transformatorType.unit;
    }
    distance(intersection) {
        if (!this.measurementGroup) {
            throw new Error("Measurement group is undefined");
        }
        if (this.isFirstPoint) {
            this.isFirstPoint = false;
            this.addFirstPoint(intersection);
        }
        else {
            this.addNextPoint(intersection);
            // Max lines riched
            if (this.lineId >= this.maxLinesAllowed) {
                this.stopMeasurement();
                return true;
            }
        }
        // Add measurement label
        const label = (0, label_1.selectLabel)(this.measureType, intersection.point, this.lineId);
        this.measurementGroup.add(label);
        return false;
    }
    height(intersection) {
        if (!this.measurementGroup) {
            throw new Error("Measurement group is undefined");
        }
        if (this.isFirstPoint) {
            this.isFirstPoint = false;
            this.addFirstPoint(intersection);
        }
        else {
            this.pointIdx += 3;
            this.addNextPoint(intersection);
            // Max lines riched
            if (this.lineId >= (this.maxLinesAllowed - 1)) {
                this.stopMeasurement();
                return true;
            }
        }
        // Add measurement label
        const label = (0, label_1.selectLabel)(this.measureType, intersection.point, this.lineId);
        this.measurementGroup.add(label);
        return false;
    }
    angle(intersection) {
        if (!this.measurementGroup) {
            throw new Error("Measurement group is undefined");
        }
        if (this.isFirstPoint) {
            this.isFirstPoint = false;
            this.addFirstPoint(intersection);
        }
        else {
            this.addNextPoint(intersection);
            // Max lines riched
            if (this.lineId >= this.maxLinesAllowed) {
                this.stopMeasurement();
                return true;
            }
            // Add measurement label
            const label = (0, label_1.selectLabel)(this.measureType, intersection.point, this.lineId);
            this.measurementGroup.add(label);
        }
        return false;
    }
    area(intersection) {
        var _a, _b;
        if (!this.measurementGroup) {
            throw new Error("Measurement group is undefined");
        }
        let isLastPoint = false;
        if (this.isFirstPoint) {
            this.isFirstPoint = false;
            this.addFirstPoint(intersection);
        }
        else {
            const positions = (_a = this.line) === null || _a === void 0 ? void 0 : _a.geometry.attributes.position.array;
            if (!positions) {
                return false;
            }
            // Do not connect when only two points drawn
            if (this.isNearStartPoint(positions, intersection.point) && this.lineId !== 0) {
                intersection.point = new three_1.Vector3(positions[0], positions[1], positions[2]);
                isLastPoint = true;
            }
            this.addNextPoint(intersection);
            if (this.lineId >= this.maxLinesAllowed) {
                this.stopMeasurement();
                return true;
            }
        }
        // Add measurement label
        const label = (0, label_1.selectLabel)(this.measureType, intersection.point, this.lineId);
        this.measurementGroup.add(label);
        if (!this.line) {
            throw new Error("Line is undefined");
        }
        const positions = this.line.geometry.attributes.position.array;
        if (isLastPoint) {
            isLastPoint = false;
            const lineLabel = (_b = this.measurementGroup) === null || _b === void 0 ? void 0 : _b.getObjectByName('lineLabel_' + this.lineId);
            if (!lineLabel) {
                throw new Error("Line label is undefined");
            }
            const areaLabelPosition = (0, label_1.areaLabelPositionCalculation)(positions, this.pointIdx);
            lineLabel.position.copy(areaLabelPosition);
            const area = (0, calculation_1.areaCalculation)(positions, this.pointIdx);
            lineLabel.element.innerHTML = area.toFixed(2) + this.transformatorType.unit; // TODO: maybe not correct
            lineLabel.element.style.backgroundColor = "#4c7850"; // TODO: color???
            this.stopMeasurement();
            return true;
        }
        return false;
    }
    addFirstPoint(intersection) {
        if (!this.measurementGroup) {
            throw new Error("Measurement group is undefined");
        }
        // Reset points and line
        this.resetPoints();
        // Create first line with break points
        this.line = this.setInitialLinePoints(this.measureType, intersection, this.points, this.maxLinesAllowed);
        this.measurementGroup.add(this.line);
        // Add measurement point
        const measurementpoint = (0, point_1.selectPoint)(this.measureType, intersection.point, this.lineId);
        this.measurementGroup.add(measurementpoint);
        // Save to measurments and add to scene
        this.measurements.push(this.measurementGroup);
        this.webgl.addObject(this.measurementGroup, true);
    }
    addNextPoint(intersection) {
        if (!this.measurementGroup) {
            throw new Error("Measurement group is undefined");
        }
        this.lineId++;
        if (!this.line) {
            return;
        }
        // Create next line
        this.setNextLinePoints(intersection, this.line);
        // Add measurement point
        const measurementpoint = (0, point_1.selectPoint)(this.measureType, intersection.point, this.lineId);
        this.measurementGroup.add(measurementpoint);
    }
    isNearStartPoint(positions, position) {
        const limit = 0.5;
        const val = new three_1.Vector3(positions[0], positions[1], positions[2]).sub(position.clone());
        const isNear = ((val.x < limit && val.x > -limit) &&
            (val.y < limit && val.y > -limit) &&
            (val.z < limit && val.z > -limit));
        return isNear;
    }
}
exports.MeasurementService = MeasurementService;
